import React, { useEffect, useState } from 'react';
import { bool, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import usStates from 'us-state-codes';
import { Button } from '../../../components';
import css from './CheckoutDetails.module.css';
import { createShippment, getRateOptions } from '../../../util/api';
import { createResourceLocatorString } from '../../../util/routes';
import routeConfiguration from '../../../routing/routeConfiguration';
import { createSlug } from '../../../util/urlHelpers';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { STEP_1, STEP_2 } from '../../../util/types';
import {
  extractShippingData,
  filterRateOptions,
  getBuyerState,
  getSellerState,
} from '../../../util/dataExtractor';

const CheckoutDetails = props => {
  const {
    form,
    shippingOrPickupDetails,
    values,
    handleSteps,
    steps,
    classes,
    buyerDefaultAddress,
    history,
    listing,
    authorData,
    cartItems,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [datas, setData] = useState(null);

  const { sellerShippingAddress = [] } =
    (!!authorData?.id && authorData?.attributes.profile.protectedData) || {};
  const sellerDefaultAddressFromUser = sellerShippingAddress.find(e => e.defaultAddress) || {};
  const { publicData } = listing.attributes || {};
  const sellerDefaultAddress = publicData?.sellerDefaultAddress?.sellerAddressLine1
    ? publicData?.sellerDefaultAddress
    : sellerDefaultAddressFromUser;

  const { packageWeight, packageLength, packageHeight, packageWidth, packageWeightInPounds } =
    publicData || {};

  // Retrieving and parsing the object
  const storedRates = sessionStorage.getItem('sessionRateData');
  const ratesArray = JSON.parse(storedRates);

  // Retrieving and parsing the object
  const storedServices = sessionStorage.getItem('sessionSelectedServices');
  const selectedData = JSON.parse(storedServices);

  const storedFormData = data => {
    sessionStorage.setItem('sessionSelectedServices', JSON.stringify(data));
  };
  const createShipment = async val => {
    const value = {
      service_code: val?.service_code,
      name: buyerDefaultAddress.userShippingName,
      phoneNumber: buyerDefaultAddress.userShippingPhoneNumber,
      address: buyerDefaultAddress.userShippingAddressLine1 || values.recipientAddressLine1,
      address2: buyerDefaultAddress.userShippingAddressLine2 || values.recipientAddressLine2,
      city: buyerDefaultAddress.userShippingCity || values.recipientCity,
      country: buyerDefaultAddress.userShippingCountry || values.recipientCountry,
      postalCode: buyerDefaultAddress.userShippingPostalCode || values.recipientPostal,
      state: getBuyerState(buyerDefaultAddress, values.recipientState),
      sellerName: sellerDefaultAddress.sellerName,
      sellerPhoneNumber: sellerDefaultAddress.sellerPhoneNumber,
      sellerAddress: sellerDefaultAddress.sellerAddressLine1,
      sellerAddress2: sellerDefaultAddress.sellerAddressLine2,
      sellerCity: sellerDefaultAddress.sellerCity,
      sellerCountry: sellerDefaultAddress.sellerCountry,
      sellerPostalCode: sellerDefaultAddress.sellerPostalCode,
      sellerState: getSellerState(sellerDefaultAddress),
      packageWeight,
    };

    const result = await createShippment(value);
    return result.shipments && result.shipments.length > 0 ? result.shipments[0].shipment_id : null;
  };

  const updateSessionData = val => {
    const sessionData = {
      ...datas,
      data: val,
      steps: STEP_2,
    };
    storedFormData(sessionData);
  };

  const redirectToCheckoutPage = () => {
    const routes = routeConfiguration();
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
        {}
      )
    );
  };
  const handleRateOptions = async () => {
    try {
      setIsLoading(true);
      const value = getRateOptionsInput();
      const data = await getRateOptions(value);

      if (data) {
        const filteredRates = filterRateOptions(data);
        const val = extractShippingData(filteredRates);
        sessionStorage.setItem('sessionRateData', JSON.stringify(filteredRates));

        // const shipmentId = await createShipment(val);

        if (val) {
          handleSteps(STEP_2);
          updateSessionData(val);
          redirectToCheckoutPage();
        } else {
          console.error('No shipments created in response data.');
        }
      }
    } catch (error) {
      console.error('An error occurred:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRateOptionsInput = () => {
    const cartItemsWeight = cartItems?.reduce(
      (accumulator, obj) => accumulator + obj.packageWeight,
      0
    );
    const cartItemsWidth = cartItems?.reduce(
      (accumulator, obj) => accumulator + parseInt(obj.packageWidth),
      0
    );
    const cartItemsHeight = cartItems?.reduce(
      (accumulator, obj) => accumulator + parseInt(obj.packageHeight),
      0
    );
    const cartItemsLength = cartItems?.reduce(
      (accumulator, obj) => accumulator + parseInt(obj.packageLength),
      0
    );
    const totalWeight = packageWeightInPounds * 16 + packageWeight;
    return {
      fromCountry: sellerDefaultAddress?.sellerCountry || sellerDefaultAddress?.userShippingCountry,
      fromPostalCode:
        sellerDefaultAddress?.sellerPostalCode || sellerDefaultAddress?.userShippingPostalCode,
      city: buyerDefaultAddress.userShippingCity || values.recipientCity,
      country: buyerDefaultAddress.userShippingCountry || values.recipientCountry,
      postalCode: buyerDefaultAddress.userShippingPostalCode || values.recipientPostal,
      packageWeight: cartItems?.length > 0 ? cartItemsWeight : totalWeight,
      packageLength: cartItems?.length > 0 ? cartItemsLength : packageLength,
      packageHeight: cartItems?.length > 0 ? cartItemsHeight : packageHeight,
      packageWidth: cartItems?.length > 0 ? cartItemsWidth : packageWidth,
      state: getBuyerState(buyerDefaultAddress, values.recipientState),
    };
  };

  useEffect(() => {
    // Retrieving and parsing the object
    const storedUser = sessionStorage.getItem('sessionSelectedServices');
    const storedSelectedData = JSON.parse(storedUser);
    setData(storedSelectedData);
  }, [isLoading]);

  const renderStep = () => {
    switch (steps) {
      case STEP_1:
        return (
          <div className={css.progressBarContent}>
            {shippingOrPickupDetails}
            <br />
            <Button
              type="button"
              inProgress={isLoading}
              onClick={() => handleRateOptions()}
              className={css.buttonNext}
              disabled={!buyerDefaultAddress}
            >
              <FormattedMessage id="CheckoutDetails.nextStep" />
            </Button>
            <hr />
          </div>
        );
      case STEP_2:
        return (
          <>
            <div className={css.PaymentcardDetails}>
              <div className={css.faqsec}>
                <div className={css.faqwrapper}>
                  <Accordion allowZeroExpanded={true}>
                    <AccordionItem>
                      <AccordionItemHeading>
                        <AccordionItemButton>
                          <div className={css.paymentHeader}>
                            <label className={css.paymentLabel}>
                              <FormattedMessage id="CheckoutDetails.heading" />
                            </label>
                          </div>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        <div className={css.addressDetails}>
                          <b>
                            {' '}
                            <FormattedMessage id="CheckoutDetails.shipTo" />:
                          </b>

                          <div>
                            <span>{buyerDefaultAddress.userShippingName}</span>,
                            <span>{buyerDefaultAddress?.userShippingAddressLine1}</span>,
                            <span>{buyerDefaultAddress?.userShippingCity}</span>,
                            <span>
                              {usStates.getStateCodeByStateName(
                                buyerDefaultAddress?.userShippingState
                              )}
                            </span>
                            ,<span>{buyerDefaultAddress?.userShippingPostalCode}</span>
                            {/* <span>{buyerDefaultAddress?.userShippingCountry}</span> */}
                          </div>
                        </div>
                        <div className={css.addressDetails}>
                          <b>
                            <FormattedMessage id="CheckoutDetails.serviceType" />:
                          </b>
                          <div>
                            <span>{datas?.data?.service_type}</span>
                          </div>
                        </div>
                        <div className={css.addressDetails}>
                          <b>
                            <FormattedMessage id="CheckoutDetails.days" />:
                          </b>
                          <div>
                            <span>{datas?.data?.delivery_days}</span>
                          </div>
                        </div>
                      </AccordionItemPanel>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>
            </div>
            <Button
              type="button"
              inProgress={isLoading}
              onClick={() => {
                handleSteps(STEP_1);
                storedFormData({ ...selectedData, steps: STEP_1 });
                const routes = routeConfiguration();
                // Redirect to CheckoutPage
                history.push(
                  createResourceLocatorString(
                    'PreCheckoutPage',
                    routes,
                    { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
                    {}
                  )
                );
              }}
              className={css.buttonBack}
            >
              <FormattedMessage id="CheckoutDetails.preStep" />
            </Button>
          </>
        );

      // add more cases for each step
      default:
        return null;
    }
  };

  return (
    <div className={classes}>
      <div className={css.faqsec}>
        <div className={css.faqwrapper}>{renderStep()}</div>
      </div>
    </div>
  );
};

CheckoutDetails.defaultProps = {
  rootClassName: null,
  className: null,
  disabled: false,
  fieldId: null,
};

CheckoutDetails.propTypes = {
  rootClassName: string,
  className: string,
  disabled: bool,
  form: object.isRequired,
  fieldId: string,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default CheckoutDetails;
